import { Badge } from "@/shared/ui/Badge";
import { formatCurrency, formatDateLocale } from "@/shared/utils/formatter.utils";
import type { InvoiceDetail } from "../types/activityOperations.types";

type InvoiceDetailBodyProps = { invoice: InvoiceDetail };

const STATUS_LABELS: Record<string, string> = {
  issued: "Émis",
  paid: "Payé",
  settled: "Soldé",
  cancelled: "Annulé",
  draft: "Brouillon",
  void: "Annulé",
};

/** Shared body for an invoice (facture / avoir): used by the detail page and the accounting drawer. */
export const InvoiceDetailBody = ({ invoice }: InvoiceDetailBodyProps) => {
  const isAvoir = invoice.kind === "avoir";

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-lg font-bold">{isAvoir ? "Avoir" : "Facture"}</h2>
        <Badge
          variant="outline"
          className={isAvoir
            ? "text-green-600 border-green-200 bg-green-50"
            : "text-red-600 border-red-200 bg-red-50"}
        >
          {isAvoir ? "Avoir (crédit)" : "Facture (débit)"}
        </Badge>
        <span className="text-sm text-muted-foreground font-mono">{invoice.number ?? invoice.id}</span>
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-xl border bg-white p-4 text-sm md:grid-cols-4">
        <div>
          <div className="text-muted-foreground">Bénéficiaire</div>
          <div className="font-medium">{invoice.holder.name ?? "—"}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Montant</div>
          <div className={`font-semibold ${isAvoir ? "text-green-600" : "text-red-600"}`}>
            {isAvoir ? "+" : "-"}
            {formatCurrency(invoice.amount)}
          </div>
        </div>
        <div>
          <div className="text-muted-foreground">Date d'effet</div>
          <div className="font-medium">{formatDateLocale(invoice.reference_date ?? invoice.created_at)}</div>
        </div>
        <div>
          <div className="text-muted-foreground">Statut</div>
          <div className="font-medium">{STATUS_LABELS[invoice.status] ?? invoice.status}</div>
        </div>
        <div className="col-span-2 md:col-span-4">
          <div className="text-muted-foreground">Libellé</div>
          <div className="font-medium">{invoice.label ?? "—"}</div>
        </div>
      </div>

      {/* Flux financier */}
      <div className="flex flex-col gap-2">
        <h3 className="font-bold">Flux financier</h3>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3 font-medium">Module</th>
                <th className="text-left p-3 font-medium">Libellé</th>
                <th className="text-right p-3 font-medium">Débit</th>
                <th className="text-right p-3 font-medium">Crédit</th>
              </tr>
            </thead>
            <tbody>
              {invoice.activite_operations.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-6 text-center text-muted-foreground">Aucune écriture</td>
                </tr>
              )}
              {invoice.activite_operations.map((operation) => (
                <tr key={operation.id} className="border-t">
                  <td className="p-3 text-muted-foreground">{operation.categorie ?? "—"}</td>
                  <td className="p-3">{operation.label ?? "—"}</td>
                  <td className="p-3 text-right tabular-nums text-red-600">
                    {operation.debit ? formatCurrency(operation.debit) : "—"}
                  </td>
                  <td className="p-3 text-right tabular-nums text-green-600">
                    {operation.credit ? formatCurrency(operation.credit) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Subvention */}
      <div className="flex flex-col gap-2">
        <h3 className="font-bold">Subvention</h3>
        {invoice.subsidy_ledger_entries.length === 0
          ? <div className="text-sm text-muted-foreground">Aucune subvention rattachée (facture libre).</div>
          : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium">Politique</th>
                    <th className="text-left p-3 font-medium">Règle</th>
                    <th className="text-left p-3 font-medium">Type</th>
                    <th className="text-right p-3 font-medium">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.subsidy_ledger_entries.map((entry) => (
                    <tr key={entry.id} className="border-t">
                      <td className="p-3">{entry.policy_name ?? "—"}</td>
                      <td className="p-3 text-muted-foreground">{entry.rule_name ?? "—"}</td>
                      <td className="p-3">{entry.entry_type === "credit" ? "Crédit" : "Débit"}</td>
                      <td className="p-3 text-right tabular-nums">{formatCurrency(entry.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </div>
  );
};
